import { useState, useEffect, useContext } from 'react';
import axios from 'axios'
import EnvContext from '../../context/EnvContext'
import * as moment from "moment-timezone"
import { gql, useSubscription } from '@apollo/client';
import { envConfig } from '../../utils/envConfig'


function getMarketScheduleBaseUrl(envSelected) {
    const liteMode = envConfig[process.env.REACT_APP_ENVIRONMENT]['lite']
    if (liteMode) {
        // For Lite Mode, we will need to use the MarketSchedule Dev version instead
        let market_api = "market-schedule-v2-dev"
        return market_api
    } else if (process.env.REACT_APP_ENVIRONMENT.startsWith("local")) {
        let market_api = "market-schedule-v2-dev"
        return market_api
    } else {
        let market_api = envSelected.startsWith("ext") ? "market-schedule-v2" : "market-schedule-v2-dev"
        return market_api
    }
}

// Calculation
export function useCalculation(service, svc_id, start_ts, end_ts) {
    const envSelected = useContext(EnvContext)
    const [urlResponse, setUrlResponse] = useState({})
    const marketAPI = getMarketScheduleBaseUrl(envSelected)

    useEffect(() => {
        let marketURL = "https://" + marketAPI + ".debesys.net/api/calculation/connection_breakdown"
        if (service === "oc") {
            marketURL += "?market_name=" + svc_id + "&start_ts=" + start_ts + "&end_ts=" + end_ts
        } else if (service === "bankalgo") {
            marketURL += "?bankalgo_connection_type=" + svc_id + "&start_ts=" + start_ts + "&end_ts=" + end_ts
        } else if (service === "fix") {
            marketURL += "?fix_session_id=" + svc_id + "&start_ts=" + start_ts + "&end_ts=" + end_ts
        }

        const fetchAPI = async () => {
            setUrlResponse({})
            axios.get(
                marketURL
            ).then(response => {
                setUrlResponse(response)
            }
            ).catch(error => {
                setUrlResponse({ "status": 404 })
            }
            )
        }
        fetchAPI()
        const interval = setInterval(() => {
            fetchAPI()
        }, 1000 * 60 * 60)
        return () => {
            setUrlResponse({})
            clearInterval(interval)
        }
    }, [marketAPI, service, svc_id, start_ts, end_ts])

    return urlResponse;
}


// FIX 
export function useFIXSchedules() {
    const envSelected = useContext(EnvContext)
    const [urlResponse, setUrlResponse] = useState({})
    const marketAPI = getMarketScheduleBaseUrl(envSelected)

    useEffect(() => {
        const marketURL = "https://" + marketAPI + ".debesys.net/api/schedule_events/fix?markets=all"
        const fetchAPI = async () => {
            setUrlResponse({})
            axios.get(
                marketURL
            ).then(response => {
                setUrlResponse(response)
            }
            ).catch(error => {
                setUrlResponse({ "status": 404 })
            }
            )
        }
        fetchAPI()
        const interval = setInterval(() => {
            fetchAPI()
        }, 1000 * 60 * 60)
        return () => {
            setUrlResponse({})
            clearInterval(interval)
        }
    }, [marketAPI])

    return urlResponse;
}

// OC
export function useOCSchedules() {
    const envSelected = useContext(EnvContext)
    const [urlResponse, setUrlResponse] = useState({})
    const marketAPI = getMarketScheduleBaseUrl(envSelected)

    useEffect(() => {
        const marketURL = "https://" + marketAPI + ".debesys.net/api/schedule_events/oc?markets=all"
        const fetchAPI = async () => {
            setUrlResponse({})
            axios.get(
                marketURL
            ).then(response => {
                setUrlResponse(response)
            }
            ).catch(error => {
                setUrlResponse({ "status": 404 })
            }
            )
        }
        fetchAPI()
        const interval = setInterval(() => {
            fetchAPI()
        }, 1000 * 60 * 60)
        return () => {
            setUrlResponse({})
            clearInterval(interval)
        }
    }, [marketAPI])

    return urlResponse;
}


// BankAlgo
export function useBASchedules() {
    const envSelected = useContext(EnvContext)
    const [urlResponse, setUrlResponse] = useState({})
    const marketAPI = getMarketScheduleBaseUrl(envSelected)

    useEffect(() => {
        const marketURL = "https://" + marketAPI + ".debesys.net/api/schedule_events/bankalgo?markets=all"
        const fetchAPI = async () => {
            setUrlResponse({})
            axios.get(
                marketURL
            ).then(response => {
                setUrlResponse(response)
            }
            ).catch(error => {
                setUrlResponse({ "status": 404 })
            }
            )
        }
        fetchAPI()
        const interval = setInterval(() => {
            fetchAPI()
        }, 1000 * 60 * 60)
        return () => {
            setUrlResponse({})
            clearInterval(interval)
        }
    }, [marketAPI])

    return urlResponse;
}

export function useMarketState() {
    const envSelected = useContext(EnvContext)
    const [urlResponse, setUrlResponse] = useState({})
    const marketAPI = getMarketScheduleBaseUrl(envSelected)

    useEffect(() => {
        const marketURL = "https://" + marketAPI + ".debesys.net/api/state/oc?markets=all"
        const fetchAPI = async () => {
            setUrlResponse({})
            axios.get(
                marketURL
            ).then(response => {
                setUrlResponse(response)
            }
            ).catch(error => {
                setUrlResponse({ "status": 404 })
            }
            )
        }
        fetchAPI()
        const interval = setInterval(() => {
            fetchAPI()
        }, 1000 * 30)

        return () => {
            setUrlResponse({})
            clearInterval(interval)
        }
    }, [marketAPI])

    return urlResponse;
}

const short_mapping = {
    "Exchange connectivity": "connect",
    "Exchange disconnectivity": "disconnect"
}

export function useMarketStateUpcoming(timezoneItems, width) {

    const [urlResponse, setUrlResponse] = useState([])
    const marketAPI = getMarketScheduleBaseUrl(process.env.REACT_APP_ENVIRONMENT)

    useEffect(() => {

        const parseSchedule = (data) => {
            let result = []
            const totalItems = Math.round((width - 50 - (148 * (timezoneItems - 1))) / 218)

            Object.keys(data.data).forEach(mktid => {
                const until = data.data[mktid].until
                const timezone = data.data[mktid].timezone
                const regex = /(\(.*\))/g
                const matching = until ? until.match(regex) : false


                if (timezone && matching && matching.length > 0 && matching[0].slice(1, -1)) {
                    const untilDateTimeMoment = moment(matching[0].slice(1, -1)).tz(timezone, true).format('YYYY-MM-DD HH:mm:ss.SSS')
                    const untilDateTime = moment(matching[0].slice(1, -1)).tz(timezone, true).valueOf()

                    let finalData = data.data[mktid]
                    finalData['untilDateTime'] = untilDateTime
                    finalData['untilDateTimeMoment'] = untilDateTimeMoment
                    finalData['timezone'] = timezone

                    if (short_mapping[finalData.next_state]) {
                        finalData['next_state'] = short_mapping[finalData.next_state]
                    }

                    result.push(finalData)
                }

            }
            )

            result.sort((a, b) => (a.untilDateTime > b.untilDateTime) ? 1 : ((b.untilDateTime > a.untilDateTime) ? -1 : 0))

            const finalResult = result.slice(0, totalItems)
            return finalResult
        }

        const marketURL = "https://" + marketAPI + ".debesys.net/api/state/oc?markets=all"
        const fetchAPI = async () => {
            axios.get(
                marketURL
            ).then(response => {
                setUrlResponse(parseSchedule(response))
            }
            ).catch(error => {
                console.log(error)
                setUrlResponse([])
            }
            )
        }
        fetchAPI()
        const interval = setInterval(() => {
            fetchAPI()
        }, 1000 * 30)

        return () => {
            setUrlResponse([])
            clearInterval(interval)
        }
    }, [marketAPI, width, timezoneItems])

    return urlResponse;
}

export function useMarketStateHoliday(noOfDays = 7) {

    const [result, setResult] = useState([])

    const MARKET_HOLIDAY_QUERY = gql`
    subscription {
        junoHolidays {
            data
            updated_at
          }
    }
    `
    const { data: sessionSubData, loading: sessionSubLoading, error: sessionSubError } = useSubscription(
        MARKET_HOLIDAY_QUERY,
    )

    useEffect(() => {


        if (!sessionSubLoading && sessionSubData && sessionSubData.junoHolidays && sessionSubData.junoHolidays.length > 0) {

            let upcomingHolidays = {}

            const allData = JSON.parse(sessionSubData.junoHolidays[0].data)

            let holidayByExch = {}

            allData.exchange.reduce((rowData, row) => {

                let session_data = {}

                const id = (row.id).replace("TT_MARKET_ID_", "")

                const roll_time = row["roll-time"].time
                const timezone = row["roll-time"].timezone
                const invalidweekeday = (row["roll-time"].filter) && (row["roll-time"].filter)['invalid-weekday'] ? (row["roll-time"].filter)['invalid-weekday'].toString() : 'Null'

                holidayByExch[id] = {
                    time: roll_time,
                    tz: timezone,
                    holidays: []
                }

                if (row["roll-time"].filter && (row["roll-time"].filter)['holiday']) {
                    // Need special handling as there might be instances where we only have a single holiday
                    let rolltimeHolidays = []
                    if (typeof (row["roll-time"].filter)['holiday'] !== Array) {
                        rolltimeHolidays.push((row["roll-time"].filter)['holiday'])
                    } else {
                        rolltimeHolidays = (row["roll-time"].filter)['holiday']
                    }
                    rolltimeHolidays.forEach(holiday => {
                        holidayByExch[id].holidays.push(holiday)
                        session_data = {
                            'id': id,
                            'uniqueId': id + holiday,
                            'roll_time': roll_time,
                            'timezone': timezone,
                            'invalidweekeday': invalidweekeday,
                            'holiday': holiday
                        }
                        rowData.push(session_data)
                    });
                } else {
                    session_data = {
                        'id': id,
                        'uniqueId': id,
                        'roll_time': roll_time,
                        'timezone': timezone,
                        'invalidweekeday': invalidweekeday,
                        'holiday': "No Holidays"
                    }
                    rowData.push(session_data)
                }

                return rowData
            }, [])

            // Parsing for Upcoming Holidays
            let monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ]
            let oneDay = 24 * 60 * 60 * 1000
            let t = new Date()
            let today = new Date(t.getFullYear() + ' ' + monthNames[t.getMonth()] + ' ' + t.getDate())
            Object.keys(holidayByExch).forEach(exchName => {
                holidayByExch[exchName] && holidayByExch[exchName].holidays && holidayByExch[exchName].holidays.length > 0 &&
                    holidayByExch[exchName].holidays.forEach(hd => {
                        hd && Array.isArray(hd) && hd.length > 0 && hd.forEach(day => {
                            let holidayDate = new Date(t.getFullYear() + ' ' + day)
                            let difference_in_days = Math.round((holidayDate - today) / oneDay)
                            if (difference_in_days >= 0 && difference_in_days <= noOfDays) {
                                if (!upcomingHolidays[exchName]) {
                                    upcomingHolidays[exchName] = {
                                        tz: holidayByExch[exchName].tz,
                                        time: holidayByExch[exchName].time,
                                        holidays: []
                                    }
                                }
                                upcomingHolidays[exchName].holidays.push(day)
                            }
                        })
                    })
            })

            // Restructure Object
            let finalTemp = {}
            Object.keys(upcomingHolidays).forEach(exchName => {
                (upcomingHolidays[exchName].holidays).forEach(holiday => {
                    if (holiday in finalTemp) {
                        finalTemp[holiday] = (finalTemp[holiday]).concat(exchName)
                    } else {
                        finalTemp[holiday] = [exchName]
                    }
                })
            })

            // Convert to String
            let finalTempString = []
            //console.log(finalTemp)
            Object.keys(finalTemp).sort().forEach(date => {
                finalTempString.push(date + " - " + (finalTemp[date]).toString())
            })
            setResult(finalTempString)
        }
    }, [noOfDays, sessionSubData, sessionSubLoading, sessionSubError])

    return result;
}
